import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; 

// Replaced external image import with a placeholder URL
import logo from "../../assets/logo.jpeg";

// 🛑 IMPORTANT: Assuming your provided changePassword API function is located here:
import { changePassword } from "../../api/reset-auth-api"; 

const ChangePassword = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Tracks if the component has completed checking for necessary credentials
  const [isCredentialCheckComplete, setIsCredentialCheckComplete] = useState(false); 

  const navigate = useNavigate();

  // Retrieve temporary credentials from Local Storage
  const emailFromFirstTimeLogin = localStorage.getItem("tempEmailForPasswordChange");
  const tempOldPasswordFromLogin = localStorage.getItem("tempOldPasswordForPasswordChange");
  const emailFromReset = localStorage.getItem("resetEmail");
  const resetCodeFromReset = localStorage.getItem("resetTokenValue");

  // Determine the context and credentials to use
  const email = emailFromFirstTimeLogin || emailFromReset;
  const oldPasswordValue = tempOldPasswordFromLogin || null;

  useEffect(() => {
    // Check if we have the necessary credentials to proceed
    const checkCredentials = () => {
        // Log a warning if credentials are missing on mount, but don't prevent mounting.
        if (!email) {
            console.warn("Missing email credential upon initial check.");
        }
        // Always set to complete after initial check
        setTimeout(() => setIsCredentialCheckComplete(true), 100); 
    };
    checkCredentials();
  }, [email]); 

  // Password strength validation checks (Unchanged)
  const validatePasswordStrength = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const errors = [];
    if (password.length < minLength) {
      errors.push("Password must be at least 8 characters long.");
    }
    if (!hasUpperCase) {
      errors.push("Password must contain at least one uppercase letter.");
    }
    if (!hasLowerCase) {
      errors.push("Password must contain at least one lowercase letter.");
    }
    if (!hasNumber) {
      errors.push("Password must contain at least one number.");
    }
    if (!hasSpecialChar) {
      errors.push("Password must contain at least one special character.");
    }
    return errors;
  };

  /**
   * Determines the correct route based on the user's role and navigates there.
   * @param {string} userRole The role field from the API response (e.g., 'admin', 'hr_officer').
   */
  const handleNavigation = (userRole) => {
    let route = "/"; // Default fallback route
    
    switch (userRole) {
        case "hr_officer":
          route = "/hr-dashboard";
          break;
        case "admin":
          route = "/admin-dashboard";
          break;
        case "program_manager":
          route = "/program-dashboard";
          break;
        case "program_director":
          route = "/projects";
          break;
        case "field_officer":
          route = "/program-staff";
          break;
        case "operations_associate":
          route = "/operations-dashboard";
          break;
        case "head_of_operations":
          route = "/head-of-operations-dashboard"; // Corrected route based on provided logic
          break;
        case "finance_officer":
          route = "/finance-dashboard";
          break;
        case "finance_manager":
          route = "/finance-manager";
          break;
        default:
          route = "/staff-dashboard";
    }
    console.log(`Password updated. Navigating to ${route} for role: ${userRole}`);
    navigate(route);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); 

    if (newPassword !== confirmNewPassword) {
      setError("New passwords do not match.");
      return;
    }

    const strengthErrors = validatePasswordStrength(newPassword);
    if (strengthErrors.length > 0) {
      return; 
    }

    try {
      setLoading(true);

      let response;
      
      // --- Use the imported `changePassword` API function ---
      response = await changePassword(
        email, 
        oldPasswordValue, // Only used for first-time login flow
        newPassword, 
        resetCodeFromReset // Only used for standard reset flow
      );
      
      // *** THE SUCCESS STATE PREVENTS THE MISSING CREDENTIALS ERROR ***
      setSuccess("Password changed successfully! Redirecting...");

      // Clear the temporary local storage keys
      localStorage.removeItem("tempEmailForPasswordChange");
      localStorage.removeItem("tempOldPasswordForPasswordChange");
      localStorage.removeItem("resetEmail");
      localStorage.removeItem("resetTokenValue");

      // --- CRITICAL STEP: Store Role and Token from API response ---
      if (response.token && response.user && response.user.role) {
        // Store the final, permanent authentication data
        localStorage.setItem("token", response.token);
        localStorage.setItem("user", JSON.stringify(response.user));
        
        // Use the role from the API response to navigate
        setTimeout(() => {
            handleNavigation(response.user.role);
        }, 1500);
      } else {
        // Fallback if API response is successful but data is missing
        console.error("API response missing token or user role for successful password change.");
        setTimeout(() => {
          navigate("/"); 
        }, 2000);
      }
    } catch (err) {
      setError(err.message || "Error changing password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Check the password strength in real-time
  const passwordStrengthErrors = validatePasswordStrength(newPassword);

  // --- RENDERING LOGIC (CRITICALLY RE-ORDERED) ---

  // 1. Initial Check/Loading UI 
  if (!isCredentialCheckComplete) {
    return (
        <div className="flex h-screen items-center justify-center bg-gray-100">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-500"></div>
        </div>
    );
  }

  // 2. Success UI (Highest priority after loading)
  if (success) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="bg-white border border-green-300 shadow-md rounded p-6 w-full max-w-md text-center">
          <div className="text-green-500 text-4xl mb-2">✅</div>
          <h2 className="text-xl font-semibold text-green-600 mb-2">
            Password Changed!
          </h2>
          <p className="text-gray-700">{success}</p>
          <p className="text-sm text-gray-500 mt-2">Redirecting shortly...</p>
        </div>
      </div>
    );
  }

  // 3. Loading UI (During API call)
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Processing password change...</p>
        </div>
      </div>
    );
  }

  // 4. Missing Credentials Error UI (Lowest priority error screen)
  // This will ONLY run if the user loads the page AND has no credentials, 
  // or if an API call *failed* and they are redirected back here without credentials, 
  // but it will NOT run if 'success' is true.
  if (!email) {
    return (
        <div className="flex h-screen items-center justify-center bg-gray-50 p-4">
            <div className="bg-white border border-red-300 shadow-md rounded p-6 w-full max-w-md text-center">
                <div className="text-red-500 text-4xl mb-2">❌</div>
                <h2 className="text-xl font-semibold text-red-600 mb-2">Access Denied</h2>
                <p className="text-gray-700">
                    It looks like you arrived here without the necessary login or reset credentials. 
                    Please return to the login page.
                </p>
                <button 
                    onClick={() => navigate('/login')} 
                    className="mt-4 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 transition-colors"
                >
                    Go to Login
                </button>
            </div>
        </div>
    );
  }
  
  // 5. Main Form UI (Default)
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-md bg-white p-8 shadow-md rounded">
        <div className="flex justify-center mb-6">
          <img 
            src={logo}
            alt="Hold the Child Logo" 
            className="h-16" 
          />
        </div>

        <h2 className="text-xl font-semibold text-gray-700 mb-4">Set New Password</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input
              type={showNewPassword ? "text" : "password"}
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setError(""); 
              }}
              className={`w-full px-3 py-2 pr-10 border rounded focus:outline-none focus:ring-2 ${
                newPassword && passwordStrengthErrors.length > 0
                  ? "border-red-500 focus:ring-red-400"
                  : "border-gray-300 focus:ring-orange-400"
              }`}
              required
            />
            <button
              type="button"
              onClick={() => setShowNewPassword((prev) => !prev)}
              className="absolute right-3 top-[38px] transform -translate-y-1/2 text-gray-600 hover:text-orange-500"
              aria-label="Toggle new password visibility"
            >
              {showNewPassword ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.27-2.944-9.544-7a10.048 10.048 0 012.34-4.021M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
                </svg>
              )}
            </button>
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <input
              type={showConfirmNewPassword ? "text" : "password"}
              placeholder="Re-enter new password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              className={`w-full px-3 py-2 pr-10 border rounded focus:outline-none focus:ring-2 ${
                confirmNewPassword && newPassword !== confirmNewPassword
                  ? "border-red-500 focus:ring-red-400"
                  : "border-gray-300 focus:ring-orange-400"
              }`}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmNewPassword((prev) => !prev)}
              className="absolute right-3 top-[38px] transform -translate-y-1/2 text-gray-600 hover:text-orange-500"
              aria-label="Toggle confirm password visibility"
            >
              {showConfirmNewPassword ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.27-2.944-9.544-7a10.048 10.048 0 012.34-4.021M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
                </svg>
              )}
            </button>
          </div>
          
          {/* Inline Validation Checklist */}
          {newPassword && (
            <div className="text-sm mt-2">
              <p className="mb-2 font-medium">Password must contain:</p>
              <ul className="list-disc list-inside space-y-1">
                <li className={!passwordStrengthErrors.includes("Password must be at least 8 characters long.") ? "text-green-600" : "text-red-500"}>
                  At least 8 characters
                </li>
                <li className={!passwordStrengthErrors.includes("Password must contain at least one uppercase letter.") ? "text-green-600" : "text-red-500"}>
                  One uppercase letter
                </li>
                <li className={!passwordStrengthErrors.includes("Password must contain at least one lowercase letter.") ? "text-green-600" : "text-red-500"}>
                  One lowercase letter
                </li>
                <li className={!passwordStrengthErrors.includes("Password must contain at least one number.") ? "text-green-600" : "text-red-500"}>
                  One number
                </li>
                <li className={!passwordStrengthErrors.includes("Password must contain at least one special character.") ? "text-green-600" : "text-red-500"}>
                  One special character
                </li>
              </ul>
            </div>
          )}

          {/* Display general error messages (e.g., passwords don't match, or API error) */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 bg-red-100 border border-red-300 text-sm px-4 py-2 rounded-md animate-fadeIn duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12c0-4.97-4.03-9-9-9s-9 4.03-9 9 4.03 9 9 9 9-4.03 9-9z" />
                </svg>
                <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-orange-500 text-white py-2 rounded-md hover:bg-orange-600 transition-colors disabled:opacity-50"
            disabled={loading || passwordStrengthErrors.length > 0 || newPassword !== confirmNewPassword || !newPassword}
          >
            Set New Password
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;